"use client";

import { Link, ToggleButton } from "@heroui/react";
import NextLink from "next/link";
import { usePathname } from "next/navigation";
import { useState } from "react";

import { Logo } from "@/components/icons";
import { ThemeSwitch } from "@/components/theme-switch";
import clsx from "clsx";
import { siteConfig } from "@/config/site";

const NavLink = ({
  href,
  children,
  className,
  isActive,
  ...props
}: {
  href: string;
  children: React.ReactNode;
  className?: string;
  isActive?: boolean;
  [key: string]: any;
}) => {
  const isExternal = href.startsWith("http");

  return (
    <Link
      className={clsx(
        "text-sm font-medium no-underline px-3 py-1.5 rounded-lg transition-all duration-150",
        isActive
          ? "text-foreground bg-default/50 font-semibold"
          : "text-default-600 hover:text-foreground hover:bg-default/30",
        className
      )}
      href={href}
      {...props}
      {...(isExternal ? { target: "_blank" } : {})}
    >
      {children}
    </Link>
  );
};

const MenuIcon = () => (
  <svg
    fill="none"
    height="24"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth="2"
    viewBox="0 0 24 24"
    width="24"
  >
    <title>Open menu</title>
    <path d="M3 12h18M3 6h18M3 18h18" />
  </svg>
);

const CloseIcon = () => (
  <svg
    fill="none"
    height="24"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth="2"
    viewBox="0 0 24 24"
    width="24"
  >
    <title>Close menu</title>
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

export const Navbar = () => {
  const pathname = usePathname();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <header className="glass-pro sticky top-0 z-50 w-full">
      {/* Content layer above glass canvas/pseudo-elements */}
      <div className="relative z-10">
        <nav className="container mx-auto max-w-6xl flex items-center justify-between px-6 py-3">
          <div className="flex items-center gap-4">
            <NextLink className="flex items-center gap-1" href="/">
              <Logo />
            </NextLink>
            <ul className="hidden lg:flex gap-1">
              {siteConfig.navItems.map((item) => (
                <li key={item.href}>
                  <NavLink href={item.href} isActive={pathname === item.href}>
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>

          <div className="hidden lg:flex items-center">
            <ThemeSwitch />
          </div>

          <div className="flex lg:hidden items-center gap-2">
            <ThemeSwitch />
            <ToggleButton
              aria-label="Toggle menu"
              className="tap-highlight-transparent"
              isIconOnly
              isSelected={isMenuOpen}
              onChange={setIsMenuOpen}
              variant="ghost"
            >
              {isMenuOpen ? <CloseIcon /> : <MenuIcon />}
            </ToggleButton>
          </div>
        </nav>

        {isMenuOpen && (
          <div className="lg:hidden border-t border-divider">
            <div className="mx-4 mt-2 flex flex-col gap-1 pb-4">
              {siteConfig.navMenuItems.map((item) => (
                <NavLink
                  key={item.href}
                  href={item.href}
                  isActive={pathname === item.href}
                >
                  {item.label}
                </NavLink>
              ))}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};
